import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Key, Sparkle, HandsPraying } from "@phosphor-icons/react";
import { PageShell } from "../components/Layout";
import PlaceAutocompleteInput from "../components/PlaceAutocompleteInput";
import PaywallGate from "../components/PaywallGate";
import SEO from "../components/SEO";
import useBirthDetailsPrefill from "../hooks/useBirthDetailsPrefill";
import api from "../lib/api";

const HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0"));
const MINUTES = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, "0"));
const inputCls = "mt-1 block w-full min-w-0 rounded-xl border-2 border-[#E8A628]/50 focus:border-[#E8A628] outline-none bg-white px-3 py-2.5 text-[15px]";
const dms = (d) => `${Math.floor(d)}°${String(Math.round((d % 1) * 60)).padStart(2, "0")}′`;
const Tile = ({ label, value, testId, hi }) => (
  <div className={`rounded-xl border p-3 ${hi ? "border-[#E8A628]" : "border-amber-200/70"}`}>
    <div className="text-xs text-muted">{label}</div>
    <div className="font-semibold text-[#3F2D1B] mt-0.5" data-testid={testId}>{value}</div>
  </div>
);

const DeityCard = ({ d }) => {
  const p = d.lord_placement;
  return (
    <section className="rounded-2xl bg-white border border-amber-200/70 p-5 sm:p-7" data-testid="br-deity-card">
      <div className="label-eyebrow text-muted">Your Pratyadhi Devata</div>
      <h2 className="font-serif-display text-3xl mt-1 text-[#3F2D1B]" data-testid="br-deity">{d.deity}</h2>
      <div className="grid sm:grid-cols-3 gap-3 mt-5 text-sm">
        <Tile label="Moon sign · 9th from Moon" value={`${d.moon_sign} · ${d.ninth_sign}`} testId="br-ninth-sign" />
        <Tile label="9th lord from Moon" value={`${d.ninth_lord}${p.retrograde ? " (R)" : ""}`} testId="br-ninth-lord" />
        <Tile label={`${d.ninth_lord} is placed in`} value={`${p.rashi} ${dms(p.degrees_in_rashi)} · ${p.nakshatra}`} testId="br-lord-sign" hi />
      </div>
    </section>
  );
};

const RemedyCard = ({ r }) => (
  <section className="rounded-2xl bg-white border-2 border-[#E8A628] p-5 sm:p-7" data-testid="br-remedy-card">
    <div className="label-eyebrow text-[#B7791F] flex items-center gap-2"><HandsPraying size={14} weight="duotone" /> Your Bhagya unlock mantra</div>
    <p className="font-serif-display text-3xl sm:text-4xl mt-3 text-[#3F2D1B]" data-testid="br-mantra">{r.mantra}</p>
    <p className="text-sm text-muted mt-2" data-testid="br-day">Begin on a {r.day} · 108 times daily for 40 days</p>
    <p className="text-[15px] leading-relaxed text-[#3F2D1B]/90 mt-5" data-testid="br-why">{r.why}</p>
    <h3 className="font-serif-display text-xl text-[#3F2D1B] mt-6">How to worship</h3>
    <ol className="mt-3 space-y-2.5" data-testid="br-steps">
      {r.steps.map((s, i) => (
        <li key={i} className="flex gap-3 text-[15px] text-[#3F2D1B]"><span className="w-6 h-6 shrink-0 rounded-full bg-[#E8A628] text-white text-xs font-bold flex items-center justify-center">{i + 1}</span>{s}</li>
      ))}
    </ol>
  </section>
);

export default function BhagyaRemedyPage() {
  const [form, setForm] = useState({ date: "", hour: "", minute: "", place: "", timezone: "" });
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null);
  const resultRef = useRef(null);

  useBirthDetailsPrefill((bd) => {
    const [hh, mm] = String(bd?.time || "").split(":");
    setForm((f) => ({ date: f.date || bd?.date || "", hour: f.hour || hh || "", minute: f.minute || mm || "",
      place: f.place || bd?.place || "", timezone: f.timezone || bd?.timezone || "" }));
  });

  const birth = { date: form.date, time: `${form.hour}:${form.minute}`, place: form.place, timezone: form.timezone };

  const compute = async (quiet) => {
    setBusy(true);
    try {
      const r = await api.post("/bhagya-remedy/compute", birth);
      setData(r.data);
      if (!quiet) setTimeout(() => resultRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not compute your chart.");
    } finally {
      setBusy(false);
    }
  };

  const submit = (e) => {
    e.preventDefault();
    if (!form.date || !form.hour || !form.minute || !form.place) return toast.error("Fill in date, time and place of birth.");
    if (!form.timezone) return toast.error("Pick the birth place from the suggestions list.");
    compute(false);
  };

  useEffect(() => { setData(null); }, [form.date, form.hour, form.minute, form.place]);

  return (
    <PageShell>
      <SEO title="Bhagya Unlock Remedy | AstroKuberChat" description="Find the Pratyadhi Devata of the 9th lord from your Moon, the deity whose mantra removes the block on your career and fortune." />
      <div className="max-w-5xl mx-auto px-5 sm:px-6 lg:px-10 pt-10 pb-20" data-testid="bhagya-remedy-page">
        <div className="label-eyebrow text-muted flex items-center gap-2"><Key size={14} weight="duotone" /> Premium · Career or bhagya stuck?</div>
        <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl font-light leading-tight mt-3">Bhagya <span className="text-gold">Unlock Remedy</span></h1>
        <div className="text-sm text-muted mt-3 max-w-3xl space-y-2" data-testid="br-intro">
          <p>There's a specific deity assigned to your Moon sign that directly controls your career security, and most astrologers never prescribe it.</p>
          <p>The 9th house from Moon governs what you need to feel safe and independent in life. When career isn't working, this is where the block sits. The Pratyadhi Devatā of the 9th lord from Moon is the exact deity whose mantra removes that block.</p>
        </div>

        <form onSubmit={submit} className="rounded-2xl bg-white border border-amber-200/70 p-5 sm:p-6 mt-8 grid sm:grid-cols-2 lg:grid-cols-[1fr_1fr_1.4fr_auto] gap-3 items-end" data-testid="br-form">
          <label className="text-xs font-semibold text-[#3F2D1B]/70">Date of birth<input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className={inputCls} data-testid="br-date" /></label>
          <div className="text-xs font-semibold text-[#3F2D1B]/70">Time of birth (24-hr)
            <div className="flex gap-2">
              <select value={form.hour} onChange={(e) => setForm({ ...form, hour: e.target.value })} className={inputCls} data-testid="br-hour"><option value="">HH</option>{HOURS.map((h) => <option key={h}>{h}</option>)}</select>
              <select value={form.minute} onChange={(e) => setForm({ ...form, minute: e.target.value })} className={inputCls} data-testid="br-minute"><option value="">MM</option>{MINUTES.map((m) => <option key={m}>{m}</option>)}</select>
            </div>
          </div>
          <label className="text-xs font-semibold text-[#3F2D1B]/70">Place of birth
            <div className="mt-1"><PlaceAutocompleteInput value={form.place} onChange={(v) => setForm({ ...form, place: v, timezone: "" })} onPick={({ name, timezone }) => setForm((f) => ({ ...f, place: name, timezone }))} placeholder="Start typing your city…" testId="br-place" /></div>
          </label>
          <button type="submit" disabled={busy} className="btn-primary rounded-full px-6 py-3 text-sm inline-flex items-center justify-center gap-2 disabled:opacity-60" data-testid="br-submit">
            <Sparkle size={15} weight="fill" /> {busy ? "Reading…" : "Find my deity"}
          </button>
        </form>

        {data && (
          <div ref={resultRef} className="mt-10 space-y-6 scroll-mt-24" data-testid="br-results">
            <DeityCard d={data} />
            {data.remedy ? <RemedyCard r={data.remedy} /> : (
              <div data-testid="br-paywall">
                <PaywallGate
                  tool="bhagya_remedy"
                  toolLabel="Bhagya Unlock Remedy"
                  birthDetails={birth}
                  variant="standalone"
                  onUnlocked={() => { toast.success("Unlocked! Revealing your mantra and worship method…"); compute(true); }}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </PageShell>
  );
}
